import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row, Select, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Search from 'antd/es/input/Search';
import { TablePaginationConfig } from 'antd/lib';
import { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import treatmentApi from '../../../apis/TreatmentApi';
import ConstantLabel from '../../../components/ConstantLabel/ConstantLabel';
import useResponsiveLayout from '../../../components/LayoutComponent/UseResponsiveLayout/UseResponsiveLayout';
import CustomContext from '../../../contexts/CustomContext';
import { Page } from '../../../models/Elements';
import { CustomAuth, Treatment } from '../../../models/Entities';
import { CropType, crops } from '../../../models/Types';
import alertService from '../../../services/AlertService';
import stringService from '../../../services/StringService';
import paginationService from '../../../services/TableService';
import TreatmentModal from './TreatmentModal/TreatmentModal';
import styles from './TreatmentsComponent.module.scss';

/**
 * Returns the treatments component.
 * @returns the treatments component.
 */
const TreatmentsComponent: React.FC<Props> = (props) => {
    /*** HOOKS ***/

    const { plotId } = props;
    const intl = useIntl();
    const [desktop] = useResponsiveLayout();
    const [form] = Form.useForm();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const [treatmentsPage, setTreatmentsPage] = useState<Page<Treatment>>();
    const [page, setPage] = useState<number>(0);
    const [filter, setFilter] = useState<Filter>({});
    const [sortField, setSortField] = useState<string>('startDate');
    const [sortOrder, setSortOrder] = useState<boolean>(false);
    const [loading, setLoading] = useState<'loading'>();
    const [treatment, setTreatment] = useState<Treatment>();
    const [treatmentModalVisible, setTreatmentModalVisible] = useState<boolean>(false);

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                if (plotId) {
                    const treatmentsPage = await treatmentApi.list(
                        page,
                        paginationService.pageSize,
                        sortField,
                        sortOrder,
                        +plotId,
                        filter.crop,
                        filter.searchText
                    );
                    setTreatmentsPage(treatmentsPage);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [plotId, page, sortField, sortOrder, filter.crop, filter.searchText, intl, auth.companyId]);

    /*** METHODS ***/

    const handleTableChange = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setPage(pagination.current ? pagination.current - 1 : 0);
        sorter.field !== undefined && setSortField(sorter.field);
        setSortOrder(sorter.order === 'ascend');
    };

    const filterTreatments = async (values: any) => {
        const filter: Filter = {
            crop: values.crop,
            searchText: values.searchText
        };
        setFilter(filter);
        setPage(0);
    };

    const list = async () => {
        try {
            setLoading('loading');
            setTreatmentModalVisible(false);
            setTreatment(undefined);
            if (plotId) {
                const treatmentsPage = await treatmentApi.list(page, paginationService.pageSize, sortField, sortOrder, +plotId, filter.crop, filter.searchText);
                setTreatmentsPage(treatmentsPage);
            }
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const showTreatmentModal = (treatment?: Treatment) => {
        setTreatment(treatment);
        setTreatmentModalVisible(true);
    };

    const closeTreatmentModal = () => {
        setTreatmentModalVisible(false);
        setTreatment(undefined);
    };

    /*** VISUAL ***/

    const cropOptions = crops.map((crop) => (
        <Select.Option key={crop} value={crop}>
            {intl.formatMessage({ id: `crop.type.${crop}` })}
        </Select.Option>
    ));

    const items = treatmentsPage ? treatmentsPage.content : [];
    const columns: ColumnsType<Treatment> = [
        {
            title: <FormattedMessage id="treatment.startDate" />,
            dataIndex: 'startDate',
            key: 'startDate',
            width: 140,
            align: 'center',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (value: Dayjs) => value && <FormattedDate value={value as any} year="numeric" month="2-digit" day="2-digit" />
        },
        {
            title: <FormattedMessage id="treatment.endDate" />,
            dataIndex: 'endDate',
            key: 'endDate',
            width: 140,
            align: 'center',
            sorter: true,
            render: (value: Dayjs) => value && <FormattedDate value={value as any} year="numeric" month="2-digit" day="2-digit" />
        },
        {
            title: <FormattedMessage id="treatment.productName" />,
            dataIndex: 'productName',
            key: 'productName',
            sorter: true
        },
        {
            title: <FormattedMessage id="treatment.productRegisterNumber" />,
            dataIndex: 'productRegisterNumber',
            key: 'productRegisterNumber',
            width: 140,
            align: 'right',
            sorter: true,
            render: (value: number | undefined) => value !== undefined && <FormattedNumber value={value} useGrouping={false} />
        },
        {
            title: <FormattedMessage id="treatment.dose" />,
            dataIndex: 'dose',
            key: 'dose',
            width: 90,
            align: 'right',
            sorter: true,
            render: (value: number | undefined, treatment: Treatment) =>
                value !== undefined && (
                    <>
                        <FormattedNumber value={value} minimumFractionDigits={0} maximumFractionDigits={2} />
                        {treatment.doseUnit && (
                            <>
                                {' '}
                                <ConstantLabel value={treatment.doseUnit} prefix="treatment.doseUnit." />
                            </>
                        )}
                    </>
                )
        },
        {
            title: <FormattedMessage id="treatment.crop" />,
            dataIndex: 'crop',
            key: 'crop',
            width: 120,
            render: (value: string | undefined) => <ConstantLabel value={value} prefix="crop.type." />
        },
        {
            title: <FormattedMessage id="treatment.cropVariety" />,
            dataIndex: 'cropVariety',
            key: 'cropVariety',
            width: 120
        },
        {
            title: <FormattedMessage id="treatment.parcel" />,
            dataIndex: 'parcel',
            key: 'parcel',
            width: 120,
            sorter: true
        },
        {
            title: <FormattedMessage id="treatment.area" />,
            dataIndex: 'area',
            key: 'area',
            width: 160,
            align: 'right',
            sorter: true,
            render: (value: number | undefined) => value !== undefined && <FormattedNumber value={value} minimumFractionDigits={0} maximumFractionDigits={2} />
        },
        {
            title: <FormattedMessage id="treatment.technician" />,
            dataIndex: 'technician',
            key: 'technician',
            sorter: true
        }
    ];

    return (
        <>
            <Form form={form} onFinish={filterTreatments} colon={false} layout="vertical" requiredMark={false}>
                <Row gutter={[24, 24]}>
                    <Col span={24} lg={14}>
                        <Form.Item name="searchText">
                            <Search
                                placeholder={intl.formatMessage({ id: 'treatments.search' })}
                                size="large"
                                className={styles.select}
                                allowClear
                                onSearch={form.submit}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={5}>
                        <Form.Item name="crop">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="treatment.crop" />}
                                className={styles.select}
                                onSelect={form.submit}
                                onClear={form.submit}
                                allowClear
                            >
                                {cropOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={5} className="table-buttons">
                        <Tooltip title={<FormattedMessage id="treatments.new.tooltip" />}>
                            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => showTreatmentModal()}>
                                {desktop && <FormattedMessage id="treatments.new" tagName="span" />}
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>
            </Form>
            <Table
                dataSource={items}
                columns={columns}
                pagination={paginationService.createPagination(treatmentsPage!)}
                rowKey="id"
                onChange={handleTableChange}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
                className="table"
                onRow={(treatment) => ({
                    onClick: () => showTreatmentModal(treatment),
                    className: styles.row
                })}
            />
            {treatmentModalVisible && plotId && <TreatmentModal plotId={plotId} treatmentId={treatment?.id} onCancel={closeTreatmentModal} onSave={list} />}
        </>
    );
};
export default TreatmentsComponent;

interface Props {
    plotId: number | undefined;
}

interface Filter {
    crop?: CropType | undefined;
    searchText?: string | undefined;
}
